import { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
  LastWebsocketJsonMessageType,
  WebsocketAction,
} from '@clients/ek/interfaces';
import { useMytaverseEvent } from '@core/dashboard/providers';
import { useMytaverse } from '@providers/MytaverseProvider';
import { default as mytaverseLogger } from '@providers/MytaverseProvider/logger';
import { useOverlayContext } from '@providers/OverlayProvider';
import { useEventContext } from '@providers/EventProvider';
import { useModalCTAContext } from '@providers/ModalCTAProvider';
import { FAMILIARISE_STATE } from '@core/dashboard/components/HomeBottomBar/constants';
import { useTrainerTools } from '@features/TrainerTools';
import { useChatState } from '@core/chat';
import useStopSession from '@features/TrainerTools/hooks/useStopSession';
import { useParticipantList } from '@core/dashboard/components/HomeBottomBar/Participants';
import { useCommunications } from '@core/communications/providers/CommunicationsProvider';
import OVERLAYS from '@constants/overlays';
import { env } from 'env';

import ParticipantScopes from '@constants/scopes';

const {
  REACT_APP_CLIENT_ENV,
  REACT_APP_CLASSROOM_EVENT_TAGS,
  REACT_APP_BASE_CLASSROOM_EVENT_TAGS,
} = env;

export const useFamiliarization = (
  lastJsonMessage: LastWebsocketJsonMessageType,
) => {
  const [isEnabled, setIsEnabled] = useState(false);

  const [isStopModal, setIsStopModal] = useState(false);
  const [isNotifyModal, setIsNotifyModal] = useState(false);

  const familiarizeStateRef = useRef<{ name: string; payload?: string } | null>(
    null,
  );

  const { t: translate } = useTranslation();

  const { gameliftStreamsSessionKey, currentEvent, setFamiliariseState } =
    useMytaverseEvent();
  const { isDebugMode, sendMessageToUnreal, user, userPermissions } =
    useMytaverse();

  const handleConfirmLogout = useStopSession();

  const isTrainer = userPermissions?.includes(
    ParticipantScopes.HubTrainingToolHubEvent,
  );

  const { isEventRun } = useEventContext();

  // Check if current event is a classroom event
  const eventTags = currentEvent?.eventTags ?? [];
  const CLASSROOM_TAGS = (REACT_APP_CLASSROOM_EVENT_TAGS || '')
    .split(',')
    .map((t) => t.toLowerCase().trim());
  const BASE_CLASSROOM_TAGS = (REACT_APP_BASE_CLASSROOM_EVENT_TAGS || '')
    .split(',')
    .map((t) => t.toLowerCase().trim());
  const isClassroomEvent = eventTags.some(
    (tag) =>
      typeof tag === 'string' && CLASSROOM_TAGS.includes(tag.toLowerCase()),
  );
  const isBaseClassroomEvent = eventTags.some(
    (tag) =>
      typeof tag === 'string' &&
      BASE_CLASSROOM_TAGS.includes(tag.toLowerCase()),
  );

  const { removeFromCurrentlyOpenOverlays } = useOverlayContext();

  const { setOpen: openParticipants, open: isParticipantsOpen } =
    useParticipantList();
  const { setOpen: setTrainerToolOpen, isObjectShareActive } =
    useTrainerTools();
  const { open: isChatOpen, setOpen: openChat } = useChatState();
  const { isSharingScreen, isSharingScreenModalOpen } = useCommunications();

  const { setValuesForModal } = useModalCTAContext();

  const handleChangeState = useCallback(
    (payload: { name: string; payload?: string } | null) => {
      setFamiliariseState(payload);
      familiarizeStateRef.current = payload;
    },
    [],
  );

  const handleFamiliarization = useCallback(
    (payload: any) => {
      const { name } = payload;

      switch (name) {
        case FAMILIARISE_STATE.RISE_HAND: {
          if (isTrainer) {
            handleChangeState({
              name: FAMILIARISE_STATE.TRAINER_TOOLS,
              payload: FAMILIARISE_STATE.RISE_HAND,
            });
          } else {
            handleChangeState({ name });
          }
          break;
        }

        case FAMILIARISE_STATE.TRAINER_TOOLS:
        case FAMILIARISE_STATE.SCREEN_SHARE:
        case FAMILIARISE_STATE['3D_OBJECTS']: {
          handleChangeState({
            name: FAMILIARISE_STATE.TRAINER_TOOLS,
            payload: name,
          });
          break;
        }

        case FAMILIARISE_STATE.VIDEO_CONTROLS: {
          handleChangeState({
            name: FAMILIARISE_STATE.TRAINER_TOOLS,
            payload: name,
          });
          break;
        }

        case FAMILIARISE_STATE.SESSION_CHAT: {
          handleChangeState({ name });

          openParticipants(false);
          removeFromCurrentlyOpenOverlays(OVERLAYS.PARTICIPANTS);

          setTrainerToolOpen(false);
          removeFromCurrentlyOpenOverlays(OVERLAYS.TRAINER_TOOLS);

          openChat(true);
          break;
        }

        case FAMILIARISE_STATE.STOP_SESSION: {
          handleChangeState({
            name: FAMILIARISE_STATE.TRAINER_TOOLS,
            payload: name,
          });
          openChat(false);

          handleConfirmLogout(true);
          break;
        }
      }
    },
    [isTrainer, removeFromCurrentlyOpenOverlays],
  );

  const handleClose = useCallback(() => {
    sendMessageToUnreal({ action: WebsocketAction.FamiliarizationInputClosed });
    //handleChangeState(null);
  }, []);

  useEffect(() => {
    if (!isEnabled) {
      return;
    }

    if (
      !isObjectShareActive &&
      familiarizeStateRef.current?.name === FAMILIARISE_STATE.TRAINER_TOOLS &&
      familiarizeStateRef.current?.payload === FAMILIARISE_STATE['3D_OBJECTS']
    ) {
      handleClose();
    }
  }, [isEnabled, isObjectShareActive]);

  useEffect(() => {
    if (!isEnabled) {
      return;
    }

    if (
      !isParticipantsOpen &&
      familiarizeStateRef.current?.name === FAMILIARISE_STATE.TRAINER_TOOLS
    ) {
      handleClose();
    }
  }, [isEnabled, isParticipantsOpen]);

  useEffect(() => {
    if (!isEnabled) {
      return;
    }

    if (
      !isChatOpen &&
      familiarizeStateRef.current?.name === FAMILIARISE_STATE.SESSION_CHAT
    ) {
      handleClose();
    }
  }, [isEnabled, isChatOpen]);

  useEffect(() => {
    if (!isEnabled) {
      return;
    }

    if (
      !isSharingScreen &&
      !isSharingScreenModalOpen &&
      familiarizeStateRef.current?.name === FAMILIARISE_STATE.TRAINER_TOOLS
    ) {
      handleClose();
    }
  }, [isEnabled, isSharingScreen, isSharingScreenModalOpen]);

  useEffect(() => {
    if (
      !lastJsonMessage ||
      REACT_APP_CLIENT_ENV !== 'EK' ||
      !isClassroomEvent
    ) {
      return;
    }

    const { action, payload, sessionKey } = lastJsonMessage;

    if (
      sessionKey &&
      sessionKey !== gameliftStreamsSessionKey &&
      !isDebugMode
    ) {
      return mytaverseLogger.debug(
        `Skip handle WS message for sessionKey: ${sessionKey}, expected sessionKey: ${gameliftStreamsSessionKey}`,
      );
    }

    const handleMessage = () => {
      switch (action) {
        case WebsocketAction.EnableFamiliarization: {
          setIsEnabled(true);
          break;
        }

        case WebsocketAction.DisableFamiliarization: {
          setIsEnabled(false);
          handleChangeState(null);
          break;
        }

        case WebsocketAction.FamiliarizationInput: {
          if (isEnabled) {
            handleFamiliarization(payload);
          }
          break;
        }

        case WebsocketAction.NotifyFamiliarizationStopped: {
          setIsNotifyModal(true);
          break;
        }
      }
    };

    handleMessage();
  }, [
    lastJsonMessage,
    gameliftStreamsSessionKey,
    isEnabled,
    isClassroomEvent,
    handleFamiliarization,
  ]);

  useEffect(() => {
    if (
      REACT_APP_CLIENT_ENV !== 'EK' ||
      !isClassroomEvent ||
      !isEventRun ||
      !currentEvent?.startTime ||
      isBaseClassroomEvent
    ) {
      return;
    }

    const stopFamiliarization = () => {
      sendMessageToUnreal({ action: WebsocketAction.StopFamiliarization });

      if (familiarizeStateRef.current?.name) {
        setIsStopModal(true);
        setIsEnabled(false);
        handleChangeState(null);
      }
    };

    let timeoutId: NodeJS.Timer | null = null;

    const now = Math.floor(Date.now() / 1000);
    const timeToStartSec = currentEvent?.startTime - now - 60;

    if (timeToStartSec > 0) {
      timeoutId = setTimeout(stopFamiliarization, timeToStartSec * 1000);
    } else {
      stopFamiliarization();
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [
    isBaseClassroomEvent,
    isClassroomEvent,
    isEventRun,
    currentEvent?.startTime,
  ]);

  useEffect(() => {
    if (!isStopModal) {
      return;
    }

    setValuesForModal({
      open: isStopModal,
      title: translate('notifications.familiarization.stopTitle'),
      message: translate('notifications.familiarization.stop'),
      altStyling: true,
      onConfirm: () => setIsStopModal(false),
      confirmButtonName: translate('continue'),
    });
  }, [isStopModal, setValuesForModal]);

  useEffect(() => {
    if (!isNotifyModal) {
      return;
    }

    setValuesForModal({
      open: isNotifyModal,
      title: translate('notifications.familiarization.notifyDisabledTitle'),
      message: translate('notifications.familiarization.notifyDisabled'),
      altStyling: true,
      onConfirm: () => setIsNotifyModal(false),
      confirmButtonName: translate('continue'),
    });
  }, [isNotifyModal, setValuesForModal]);
};
